import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { LoadingButton } from '@mui/lab';
import { Card, IconButton, InputAdornment, Stack } from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { useToaster } from 'src/utils/toaster/toasterContext';

export default function AccountChangePassword() {
  const { t: translate } = useTranslation();
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  const { showToast } = useToaster();
  const defaultValues = {
    oldPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  };

  const successCallback = (data, message) => {
    showToast(message, 's');
    reset();
  };

  const failureCallback = (message) => {
    showToast(message, 'e');
    reset();
  };

  const methods = useForm({
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async () => {
    if (oldPassword === '') {
      showToast(translate('profile.change_password.old_password_cannot_be_empty'), 'e');
      return;
    }
    if (oldPassword === newPassword) {
      showToast(translate('profile.change_password.same_password_error'), 'e');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      showToast(translate('profile.change_password.password_mismatch_error'), 'e');
      return;
    }

    let changePasswordForm = {
      old_password: oldPassword,
      new_password: newPassword,
    };

    let postProps = {
      url: userModuleURL.updatePasswordRequest,
      body: changePasswordForm,
      successCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };

  const handleInputChange = (event) => {
    let value = event.target.value;
    let name = event.target.name;
    switch (name) {
      case 'oldPassword':
        setOldPassword(value);
        break;
      case 'newPassword':
        setNewPassword(value);
        break;
      case 'confirmNewPassword':
        setConfirmNewPassword(value);
        break;
      default:
        break;
    }
  };

  return (
    <Card sx={{ p: 3 }}>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3} alignItems="flex-end">
          <RHFTextField
            name="oldPassword"
            type={showOldPassword ? 'text' : 'password'}
            value={oldPassword}
            label={translate('profile.change_password.old_password_label')}
            onChange={handleInputChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowOldPassword(!showOldPassword)} edge="end">
                    {showOldPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <RHFTextField
            name="newPassword"
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            label={translate('profile.change_password.new_password_label')}
            onChange={handleInputChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                    {showNewPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <RHFTextField
            name="confirmNewPassword"
            type={showConfirmNewPassword ? 'text' : 'password'}
            value={confirmNewPassword}
            label={translate('profile.change_password.confirm_password_label')}
            onChange={handleInputChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)} edge="end">
                    {showConfirmNewPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            {translate('common.save_changes')}
          </LoadingButton>
        </Stack>
      </FormProvider>
    </Card>
  );
}
